
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { openPermissionGuidanceDialog } from '../../../../device-selection';
import LanguagesDropdown from '../../../../languages-component/components/LanguagesDropdown';
import {
    AudioSettingsButton,
    VideoSettingsButton
} from '../../../../toolbox/components/web';
import { requestMediaPermissions } from '../../../../toolbox/utils/permissionUtils';
import { Avatar } from '../../../avatar';
import { devicePermissionsChanged } from '../../../devices';
import { Icon, IconSettings, IconWarning } from '../../../icons';
import MeetHourJS from '../../../lib-meet-hour';
import { MEDIA_TYPE, Video } from '../../../media';
import { getLocalParticipant } from '../../../participants';
import { connect } from '../../../redux';
import Tooltip from '../../../tooltip/components/Tooltip';
import {
    getLocalAudioTrack,
    getLocalMHAudioTrack,
    getLocalVideoTrack,
    isLocalTrackMuted
} from '../../../tracks';

const MHTrackEvents = MeetHourJS?.events?.track;


export type Props = {

    /**
     * Flag signaling the visibility of camera preview.
     */
    videoMuted: boolean,

    /**
     * Flag signaling whether audio is muted.
     */
    audioMuted?: boolean,

    /**
     * Local audio track object.
     */
    _audioTrack?: ?Object,

    /**
     * Local MeetHour audio track object.
     */
    _mhTrack?: ?Object,

    /**
     * ShowAvatar.
     */
    showAvatar: boolean,

    /**
     * Name of the user.
     */
    name: String,

    /**
     * The MHLocalTrack to display.
     */
    videoTrack: ?Object,

    /**
     * HasPermissions.
     */
    hasPermissions: ?Object,
};

// const participantColor = props._participantColor || assignColorToParticipant('mock_id');

const styleHeight = {
    minHeight: '100%',
    borderRadius: '10px'
};

// const styleBackground = {
//     backgroundColor: participantColor
// };

const styledAudio = {
    display: 'inline-block',
    margin: '0px'
};

const styleDivVideo = {
    background: '0 0',
    ...styledAudio,
    maxHeight: '344px'
};


/**
 * Updates the large video shadow effect, matching LargeVideo audio level ripple.
 *
 * @param {number} level - The audio level between 0 and 1.
 * @returns {string} The CSS box-shadow value.
 */
function _updateLargeVideoShadow(level) {
    const scale = 2;
    const primaryColor = (typeof interfaceConfig !== 'undefined' && interfaceConfig.AUDIO_LEVEL_PRIMARY_COLOR)
        || 'rgba(255,255,255,0.4)';
    const secondaryColor = (typeof interfaceConfig !== 'undefined' && interfaceConfig.AUDIO_LEVEL_SECONDARY_COLOR)
        || 'rgba(255,255,255,0.2)';

    // Internal circle audio level.
    const int = {
        level: level > 0.15 ? 20 : 0,
        color: primaryColor
    };

    // External circle audio level.
    const ext = {
        level: parseFloat(
            ((int.level * scale * level) + int.level).toFixed(0)),
        color: secondaryColor
    };

    // Internal blur.
    int.blur = int.level ? 2 : 0;

    // External blur.
    ext.blur = ext.level ? 6 : 0;

    return [
        `0 0 ${int.blur}px ${int.level}px ${int.color}`,
        `0 0 ${ext.blur}px ${ext.level}px ${ext.color}`
    ].join(', ');
}

/**
 * Component showing the video preview and device status.
 *
 * @param {Props} props - The props of the component.
 * @returns {ReactElement}
 */
function Preview(props: Props) {
    const {
        videoMuted,
        videoTrack,
        showAvatar,
        name,
        hasPermissions,
        _participantColor,
        hideOptions,
        toggleHideOptions,
        virtualBackgroundDialog,
        audioMuted,
        _audioTrack,
        _mhTrack,
        prejoinJoinError
    } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [ audioLevel, setAudioLevel ] = useState(0);
    const [ isSpeaking, setIsSpeaking ] = useState(false);
    const speakingTimeoutRef = useRef(null);

    useEffect(() => {
        const mhTrack = _audioTrack?.mhTrack || _mhTrack;

        if (!mhTrack || audioMuted) {
            if (speakingTimeoutRef.current) {
                clearTimeout(speakingTimeoutRef.current);
                speakingTimeoutRef.current = null;
            }
            setAudioLevel(0);
            setIsSpeaking(false);

            return;
        }

        const handleAudioLevel = level => {
            if (audioMuted) {
                if (speakingTimeoutRef.current) {
                    clearTimeout(speakingTimeoutRef.current);
                    speakingTimeoutRef.current = null;
                }
                setAudioLevel(0);
                setIsSpeaking(false);

                return;
            }

            const rawLevel = typeof level === 'number' && !isNaN(level)
                ? Math.max(0, Math.min(level, 1))
                : 0;

            const sensitiveLevel = rawLevel > 0 ? Math.min(rawLevel * 2.5, 1) : 0;
            const speaking = sensitiveLevel >= 0.15;

            if (speaking) {
                if (speakingTimeoutRef.current) {
                    clearTimeout(speakingTimeoutRef.current);
                }

                speakingTimeoutRef.current = setTimeout(() => {
                    setIsSpeaking(false);
                    setAudioLevel(0);
                }, 300);

                setAudioLevel(sensitiveLevel);
                setIsSpeaking(true);
            } else if (sensitiveLevel > 0) {
                setAudioLevel(sensitiveLevel);
            } else {
                setAudioLevel(0);
            }
        };

        const trackEvents = MHTrackEvents || MeetHourJS?.events?.track;

        if (trackEvents && typeof mhTrack.on === 'function') {
            mhTrack.on(trackEvents.TRACK_AUDIO_LEVEL_CHANGED, handleAudioLevel);
        }

        return () => {
            if (trackEvents && typeof mhTrack.off === 'function') {
                mhTrack.off(trackEvents.TRACK_AUDIO_LEVEL_CHANGED, handleAudioLevel);
            }
            if (speakingTimeoutRef.current) {
                clearTimeout(speakingTimeoutRef.current);
                speakingTimeoutRef.current = null;
            }
            setAudioLevel(0);
            setIsSpeaking(false);
        };
    }, [ _audioTrack?.mhTrack, _mhTrack, audioMuted ]);

    const dynamicShadow = _updateLargeVideoShadow(isSpeaking && !audioMuted ? audioLevel : 0);

    const participantColor = _participantColor || '#4b6790';
    const isSettingsPermissionDenied = hasPermissions?.audio === false || hasPermissions?.video === false;
    const isPermissionDenied = isSettingsPermissionDenied;
    const isDeviceMuted = videoMuted || audioMuted;
    const isBothMuted = videoMuted && audioMuted;

    const onSettingsClick = () => {
        const deniedDevices = [];

        if (hasPermissions?.audio === false) {
            deniedDevices.push('audio');
        }

        if (hasPermissions?.video === false) {
            deniedDevices.push('video');
        }

        if (deniedDevices.length) {
            dispatch(openPermissionGuidanceDialog(deniedDevices));

            return;
        }

        toggleHideOptions();
    };

    const onUserAudioclick = () => {
        const _permissionsListener = permissions => {
            dispatch(devicePermissionsChanged(permissions));
        };

        const permissionsApi = navigator?.permissions;

        if (typeof permissionsApi?.query === 'function') {
            permissionsApi.query({ name: 'microphone' }).catch(() => {
                // Ignore unsupported browser implementations.
            });
        }
        const { mediaDevices } = MeetHourJS;

        // Query once only. The base/devices middleware already turns every PERMISSIONS_CHANGED into
        // devicePermissionsChanged; adding a listener here on every click leaked one per click on the lib singleton.
        Promise.all([ mediaDevices.isDevicePermissionGranted('audio'), mediaDevices.isDevicePermissionGranted('video') ])
            .then(results => {
                _permissionsListener({
                    audio: results[0],
                    video: results[1]
                });
            })
            .catch(() => {
                // Ignore errors.
            });
    };

    const onUserVideoclick = () => {
        const _permissionsListener = permissions => {
            dispatch(devicePermissionsChanged(permissions));
        };
        const { mediaDevices } = MeetHourJS;

        Promise.all([ mediaDevices.isDevicePermissionGranted('audio'), mediaDevices.isDevicePermissionGranted('video') ])
            .then(results => {
                _permissionsListener({
                    audio: results[0],
                    video: results[1]
                });
            })
            .catch(() => {
                // Ignore errors.
            });
    };

    const onRequestPermissions = async () => {
        try {
            const deniedDevices = [];

            // Each device is requested on its own. audio/video both default to true, so the calls below used to
            // request both devices every time: a user with only a microphone had the audio request fail on the
            // missing camera and ended up with no permissions at all.
            const audioResult = await requestMediaPermissions({ audio: true,
                video: false });

            if (audioResult.showGuidance) {
                deniedDevices.push('audio');
            }

            // Request video permission
            const videoResult = await requestMediaPermissions({ audio: false,
                video: true });

            if (videoResult.showGuidance) {
                deniedDevices.push('video');
            }

            // If permissions were denied, show the guidance dialog for the devices that actually failed.
            if (deniedDevices.length > 0) {
                dispatch(openPermissionGuidanceDialog(deniedDevices));
            }
        } catch {
            // Ignore errors during permission request
        }
    };


    return (
        <div id = 'preview'>
            <div
                className = 'user-preview'
                style = {{ background: participantColor }}>
                {(videoMuted || videoTrack === undefined) && showAvatar && (
                    <div style = {{ ...styleHeight }}>
                        <div className = 'premeeting-screen-avatar-container'>
                            <div
                                className = 'dynamic-shadow'
                                style = {{
                                    boxShadow: dynamicShadow
                                }} />
                            <Avatar
                                audioLevel = { audioLevel }
                                className = 'premeeting-screen-avatar'
                                displayName = { name }
                                dynamicColor = { false }
                                participantId = 'local'
                                size = { 120 } />
                        </div>
                    </div>
                )}
                {!videoMuted && videoTrack
                    && <Video videoTrack = {{ mhTrack: videoTrack }} />
                }
                <div className = { interfaceConfig?.applyMeetingSettings === true ? 'branded-conference-toolbox toolbox-content-items1' : 'toolbox-content-items1' }>
                    <div
                        onClick = { hasPermissions.audio ? undefined : onUserAudioclick }
                        style = { styledAudio }>
                        <AudioSettingsButton
                            dispatch = { dispatch }
                            key = 'asb'
                            visible = { true } />
                    </div>
                    <div
                        onClick = { hasPermissions.video ? undefined : onUserVideoclick }
                        style = { styleDivVideo }>
                        <VideoSettingsButton
                            dispatch = { dispatch }
                            key = 'vsb'
                            visible = { true } />
                    </div>
                    <Tooltip
                        content = { t('toolbar.accessibilityLabel.Settings') }
                        position = { 'top' }>
                        <div
                            className = { `IconWarningButtonParent toolbox-icon overflow-btn-pre-meeting-toolbox ${hideOptions ? null : 'toggled'}` }
                            onClick = { onSettingsClick }>
                            {isSettingsPermissionDenied && (
                                <div className = 'IconWarningButton warning-icon'>
                                    <Icon src = { IconWarning } />
                                </div>
                            )}
                            <Icon src = { IconSettings } />
                        </div>
                    </Tooltip>
                    {virtualBackgroundDialog}
                    {interfaceConfig?.applyMeetingSettings === true
                        && interfaceConfig?.disablePrejoinHeader && (
                        <div className = 'language-button-container'>
                            <Tooltip
                                content = { t('settings.languageSettings') }
                                position = { 'top' }>
                                <div className = 'settings-button-container settings-button-container-language-dropdown'>
                                    <LanguagesDropdown />
                                </div>
                            </Tooltip>
                        </div>
                    )}
                </div>
                {isPermissionDenied && (
                    <div className = 'permission-overlay'>
                        <div className = 'permission-overlay-content'>
                            <div className = 'permission-overlay-text'>
                                {t('deviceSelection.permissionDeniedPrompt')}
                            </div>
                            <button
                                className = 'permission-overlay-button'
                                onClick = { onRequestPermissions }>
                                {t('deviceSelection.allowPermissionsButton')}
                            </button>
                        </div>
                    </div>
                )}
                {!isPermissionDenied && isDeviceMuted && (
                    <div className = 'device-muted-overlay'>
                        <div className = 'device-muted-overlay-content'>
                            {isBothMuted && (
                                <div className = 'device-muted-text'>
                                    {t('deviceSelection.microphoneAndCameraOff')}
                                </div>
                            )}
                            {!isBothMuted && audioMuted && !videoMuted && (
                                <div className = 'device-muted-text'>
                                    {t('deviceSelection.microphoneOff')}
                                </div>
                            )}
                            {!isBothMuted && videoMuted && !audioMuted && (
                                <div className = 'device-muted-text'>
                                    {t('deviceSelection.cameraOff')}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!isPermissionDenied && prejoinJoinError && (
                    <div className = 'device-muted-overlay'>
                        <div className = 'device-muted-overlay-content'>
                            <div className = 'device-muted-text'>
                                {prejoinJoinError}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Props} ownProps - The own props of the component.
 * @returns {Props}
 */
function _mapStateToProps(state, ownProps) {
    const { permissions = {} } = state['features/base/devices'];
    const participant = getLocalParticipant(state);
    const tracks = state['features/base/tracks'];
    const _audioTrack = getLocalAudioTrack(tracks);
    const _mhTrack = getLocalMHAudioTrack(state);
    const isAudioMuted = Boolean(state['features/base/media']?.audio?.muted)
        || isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO);

    return {
        hasPermissions: permissions,
        _participantColor: participant?.color,
        videoMuted: ownProps.videoTrack
            ? ownProps.videoMuted
            : state['features/base/media'].video.muted === 0,
        audioMuted: isAudioMuted,
        _audioTrack,
        _mhTrack,
        prejoinJoinError: state['features/prejoin']?.joinErrorMessage,
        videoTrack:
            ownProps.videoTrack
            || (getLocalVideoTrack(tracks) || {}).mhTrack
    };
}

export default connect(_mapStateToProps)(Preview);
